import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  LocalDining,
  AccessTime,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { OrderService } from '../services/orderService.js';

export const KitchenPage = () => {
  const navigate = useNavigate();
  const { outletId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const fetchOrders = useCallback(async () => {
    if (!outletId) return;
    try {
      const data = await OrderService.fetchOrders(outletId, 'Active');
      // Sort oldest first
      const sorted = [...data].sort(
        (a, b) => new Date(a.CreatedAt) - new Date(b.CreatedAt)
      );
      setOrders(sorted);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    fetchOrders();
    pollRef.current = setInterval(fetchOrders, 7000);
    return () => clearInterval(pollRef.current);
  }, [fetchOrders]);

  const getTimeElapsed = (createdAt) => {
    if (!createdAt) return '';
    const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m ago`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', bgcolor: '#EBEBEF', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* ─── Header ─── */}
      <Box
        sx={{
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          border: '1px solid rgba(0,0,0,0.06)',
          p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton onClick={() => navigate('/home')} size="small">
          <ArrowBack />
        </IconButton>
        <LocalDining sx={{ color: '#4527A0' }} />
        <Typography variant="h6" fontWeight={700}>
          Kitchen
        </Typography>
        <Chip label={`${orders.length} active orders`} size="small" color="primary" variant="outlined" sx={{ ml: 'auto' }} />
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ─── Orders List (vertical, oldest first) ─── */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {orders.length === 0 ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
            <LocalDining sx={{ fontSize: 64, color: '#CCC', mb: 2 }} />
            <Typography color="text.secondary" fontSize="1.1rem">
              No active orders
            </Typography>
          </Box>
        ) : (
          orders.map((order) => {
            const items = (order.ItemNames || []).map((name, idx) => ({
              name,
              qty: (order.Quantity || [])[idx] || 1,
            }));
            const fallbackItems = (!order.ItemNames || order.ItemNames.length === 0)
              ? (order.items || []).map((item) => ({
                  name: item.item_name || item.name,
                  qty: item.quantity || 1,
                }))
              : [];
            const allItems = items.length > 0 ? items : fallbackItems;

            return (
              <Box
                key={order.OrderNum || order.OrderID}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '14px',
                  border: '1px solid rgba(0,0,0,0.08)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  overflow: 'hidden',
                }}
              >
                {/* Card header */}
                <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography fontWeight={800} fontSize="1.1rem" color="primary.main">
                    #{order.OrderNum}
                  </Typography>
                  {order.CreatedAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                      <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography fontSize="0.75rem" color="text.secondary">
                        {getTimeElapsed(order.CreatedAt)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Items container */}
                <Box sx={{ mx: 1.5, mb: 1.5, borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)' }}>
                  {allItems.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1,
                        bgcolor: idx % 2 === 0 ? '#EBF4FF' : '#fff',
                      }}
                    >
                      <Typography fontSize="0.85rem" fontWeight={600} color="text.primary">
                        {item.name}
                      </Typography>
                      <Typography fontSize="0.85rem" fontWeight={700} color="text.secondary" sx={{ minWidth: 32, textAlign: 'right' }}>
                        ×{item.qty}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};
